import * as React from "react";
import PdfPrinter from "pdfmake";
import { useEffect, useState, useRef } from "react";
import { ehrActions } from "../../core/actions";
import { jsonSchema } from "../../shared/schema";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";
import { useOrganization } from "../../configurations/OrganizationContext";
import { labActions } from "../../core/actions";
import { PRINT_FILE, PRINT_PREVIEW } from "../constants";
import { printPDFFile } from "../../components/erp-billing/ERPActions/erpActions";
import { convertDocDefinitionToHTML } from "../../utils/docDefDischarge";
import { convertDocDefinitionToHTMLManaswini } from "../../utils/docDefManaswini";
import {
ORG_CODES
} from "../../shared/constants";
var fonts = require("pdfmake/build/vfs_fonts");

function LabReportPrint(props) {
  const dispatch = useDispatch();
  const org = useOrganization();
  const [patientInfo] = useState(props.selectedPatient);
  const [doctorDetails] = useState(props.selectedPatient.person);
  const [printPreviewType] = useState(props.printPreviewType);

  const generateCalled = useRef(false);
  // Track which prescription indices have completed
  const completedRows = useRef({});
  const totalPrescriptions = useRef(0);

  let location = "";
  let validators = "";

  const heading =
    org?.orgCode === ORG_CODES.MANASWINI
      ? ["Test ", "Result", "Units", "Ref. Range", "Comment"]
      : ["Test Name ", "Observed Value", "Unit", "Reference Range"];

  const now = new Date();
  const formattedDate = `${getNumberWithSuffix(now.getDate())} ${now.toLocaleString("default", { month: "short" })} ${now.getFullYear()}`;

  function getNumberWithSuffix(number) {
    const suffixes = ["th", "st", "nd", "rd"];
    const suffix = number % 100;
    return number + (suffixes[(suffix - 20) % 10] || suffixes[suffix] || suffixes[0]);
  }

  let reportArray = [];

  useEffect(() => {
    generateCalled.current = false;
    completedRows.current = {};

    if (
      props.selectedPatient?.type === "InPatient" &&
      props.selectedPatient.room?.roomName
    ) {
      location = props.selectedPatient.room.roomName;
    } else {
      location = "opd";
    }

    if (!props.prescriptionList?.length) return;

    totalPrescriptions.current = props.prescriptionList.length;
    const newRows = new Array(props.prescriptionList.length);

    props.prescriptionList.forEach((item, index) => {
      const formatDispacthDate = convertUTCToISTDateTime(item.udt);
      const formatsampleDate = convertUTCToISTDateTime(item.billDate);

      newRows[index] = {
        reportNo: item.reportNo ?? "",
        billNo: item.billNumber ?? "",
        sampleDate: formatsampleDate ?? "",
        category: [],
        value: [],
        comment: [],
        footer: [],
        varifyName: item.approver ?? "",
        dispatchDate: formatDispacthDate,
      };

      const templateId = item.templateId;
      const compositionId = item.compositionId;
      validators = item.shortdesc;

      dispatch(
        labActions.getCategoryByTemplateId(templateId, async (resCb1) => {
          if (await resCb1) {
            newRows[index]["category"].push(resCb1.category ?? resCb1);
          }
        })
      );

      dispatch(
        ehrActions.getCompositionByVid({}, compositionId, props.ehrid, async (resCb) => {
          if (await resCb) {
            const form = await jsonSchema.getSchemaFromCanonicalJson(resCb, templateId, "editComposition");
            formatLabReport(form, templateId);

            newRows[index]["value"].push(heading);
            const reportData = reportArray[templateId] || {};

            // ── Collect all tests flat ──
            const allTests = [];
            for (let entry in reportData) {
              for (let ent of reportData[entry]) {
                if (!ent.testName) continue;
                allTests.push(ent);
              }
            }

            // ── Pre-register in order ──
            const packageMap = new Map();
            const packageOrder = [];

            for (let ent of allTests) {
              if (ent.boldname === true) {
                const headerRow = org?.orgCode === ORG_CODES.MANASWINI
                  ? [
                      { text: ent.testName, bold: true, boldname: true },
                      { text: "", bold: false },
                      { text: "", bold: false },
                      { text: "", bold: false },
                      { text: "", bold: false },
                    ]
                  : [
                      { text: ent.testName, bold: true, boldname: true },
                      { text: "", bold: false },
                      { text: "", bold: false },
                      { text: "", bold: false },
                    ];

                if (!packageMap.has(ent.testName)) {
                  packageMap.set(ent.testName, {
                    footerName: ent.testName,
                    isPackage: true,
                    testRows: [{ type: "test", row: headerRow }],
                  });
                  packageOrder.push(ent.testName);
                }
              } else {
                if (!packageMap.has(ent.testName)) {
                  packageMap.set(ent.testName, {
                    footerName: ent.testName,
                    isPackage: false,
                    testRows: [],
                    __placeholder: true,
                  });
                  packageOrder.push(ent.testName);
                }
              }
            }

            const nonBoldTests = allTests.filter(t => !t.boldname);
            const totalToResolve = nonBoldTests.length;
            let resolvedCount = 0;

            const tryFinalize = () => {
              if (resolvedCount < totalToResolve) return;

              const groups = packageOrder
                .map(key => packageMap.get(key))
                .filter(g => g && g.testRows.length > 0);

              console.log("[DEBUG] groups FULL:", JSON.stringify(groups.map(g => ({
                footerName: g.footerName,
                isPackage: g.isPackage,
                tests: g.testRows.map(r => r.row?.[0]?.text ?? ""),
              })), null, 2));

              processGroupsSequentially(groups, index, newRows);
            };

            // ── Resolve non-bold tests ──
            for (let ent of nonBoldTests) {
              const row = org?.orgCode === ORG_CODES.MANASWINI
                ? [
                    { text: ent.testName, bold: false },
                    { text: ent.result, bold: ent.resultBold === true },
                    { text: ent.units, bold: false },
                    { text: ent.refRange, bold: false },
                    { text: ent.comment ?? "", bold: false },
                  ]
                : [
                    { text: ent.testName, bold: false },
                    { text: ent.result, bold: ent.resultBold === true },
                    { text: ent.units, bold: false },
                    { text: ent.refRange, bold: false },
                  ];

              const commentRow = (ent.comment && org?.orgCode !== ORG_CODES.MANASWINI)
                ? [{ text: ent.comment, isComment: true }, { text: "" }, { text: "" }, { text: "" }]
                : null;

              dispatch(
                labActions.getFooterByTestName(ent.testName, (res) => {
                  const pkgName = res?.packageName ?? ent.testName;

                  if (pkgName !== ent.testName) {
                    const orderIdx = packageOrder.indexOf(ent.testName);
                    if (packageMap.has(pkgName)) {
                      packageMap.get(pkgName).testRows.push({ type: "test", row });
                      if (commentRow) packageMap.get(pkgName).testRows.push({ type: "comment", row: commentRow });
                      if (orderIdx !== -1) packageOrder.splice(orderIdx, 1);
                      packageMap.delete(ent.testName);
                    } else {
                      packageMap.delete(ent.testName);
                      packageMap.set(pkgName, {
                        footerName: pkgName,
                        isPackage: false,
                        testRows: [{ type: "test", row }],
                      });
                      if (commentRow) packageMap.get(pkgName).testRows.push({ type: "comment", row: commentRow });
                      if (orderIdx !== -1) packageOrder[orderIdx] = pkgName;
                    }
                  } else {
                    packageMap.get(ent.testName).testRows.push({ type: "test", row });
                    if (commentRow) packageMap.get(ent.testName).testRows.push({ type: "comment", row: commentRow });
                  }

                  resolvedCount++;
                  tryFinalize();
                })
              );
            }

            if (totalToResolve === 0) tryFinalize();
          }
        })
      );
    });
  }, []);

  // ── Process groups sequentially per prescription ──
  function processGroupsSequentially(groups, index, newRows) {
    const processGroup = (groupIndex) => {
      if (groupIndex >= groups.length) {
        // ✅ This prescription is done — finalize its rows
        finalizeRow(index, newRows);
        return;
      }

      const group = groups[groupIndex];

      // spacer before
      newRows[index]["value"].push([
        "", "", "", "",
        ...(org?.orgCode === ORG_CODES.MANASWINI ? [""] : []),
      ]);

      // test rows
      for (let item of group.testRows) {
        newRows[index]["value"].push(item.row);
      }

      // spacer after
      newRows[index]["value"].push(
        org?.orgCode === ORG_CODES.MANASWINI
          ? ["", " ", " ", " ", " "]
          : ["", " ", " ", " "]
      );

      if (!group.footerName) {
        processGroup(groupIndex + 1);
        return;
      }

      const pushFooterAndNext = (footerText) => {
        if (footerText) {
          newRows[index]["value"].push([
            {
              text: footerText,
              fontSize: 9,
              colSpan: org?.orgCode === ORG_CODES.MANASWINI ? 5 : 4,
              color: "#333333",
            },
            "", "", "",
            ...(org?.orgCode === ORG_CODES.MANASWINI ? [""] : []),
          ]);
        }
        processGroup(groupIndex + 1);
      };

      dispatch(
        labActions.getCategoryByTemplateId(group.footerName, (resCb2) => {
          pushFooterAndNext(resCb2?.footer ?? null);
        })
      );
    };

    processGroup(0);
  }

  // ── finalizeRow: called per prescription when its groups are done ──
  function finalizeRow(index, newRows) {
    newRows[index]["value"] = newRows[index]["value"].map((row) =>
      row.map((cell) => {
        if (cell === undefined || cell === null) return { text: "", fontSize: 9 };
        if (typeof cell === "object" && "text" in cell) {
          const mapped = {
            text: cell.text === undefined ? "" : cell.text,
            fontSize: cell.boldname ? 10 : 9,
            bold: cell.resultBold === true || cell.bold === true,
          };
          if (cell.colSpan !== undefined) mapped.colSpan = cell.colSpan;
          if (cell.color !== undefined) mapped.color = cell.color;
          if (cell.isComment === true) mapped.isComment = true;
          return mapped;
        }
        return { text: cell, fontSize: 9 };
      })
    );

    // ✅ Mark this prescription index as complete
    completedRows.current[index] = true;
    const completedCount = Object.keys(completedRows.current).length;

    console.log(`[FINALIZE] index ${index} done. ${completedCount}/${totalPrescriptions.current} complete`);

    // ✅ Only generate when ALL prescriptions are finalized
    if (
      completedCount === totalPrescriptions.current &&
      !generateCalled.current
    ) {
      generateCalled.current = true;
      console.log("[GENERATE] All prescriptions ready, generating report...");
      // Pass all rows, generate using last index
      generateReport(totalPrescriptions.current - 1, newRows);
    }
  }

  function formatLabReport(form, templateId) {
    const reportObject = form.properties;
    const reports = Object.keys(reportObject);
    const testArray = [];

    for (let report of reports) {
      if (report === "COMMENT") {
        testArray.push({ comment: reportObject[report].default });
        continue;
      }

      const reportProps = reportObject[report].properties;
      let testName = report;
      let result = "", units = "", refRange = "", comment = "";
      let bold = false;
      let boldname = false;
      let resultBold = false;

      if (reportProps && Object.keys(reportProps).length > 0) {
        for (let it in reportProps) {
          const val = reportProps[it].default;
          if (it === "Result" || it === "Analyte result") {
            result = val === -1 ? "--" : val;
          } else if (it === "Units" || it === "Unit") {
            units = val;
          } else if (it === "Ref. Range" || it === "Reference range guidance") {
            refRange = val;
          } else if (it === "Comment" && org?.orgCode === ORG_CODES.MANASWINI) {
            comment = val === "No Option Selected" ? "--" : val;
          }
        }
        if (result === 0 || result === "") continue;
      } else {
        boldname = true;
        bold = true;
      }

      const { min, max } = parseRange(refRange);
      const numResult = parseFloat(result);
      const hasMin = min !== null && min !== undefined && !isNaN(min);
      const hasMax = max !== null && max !== undefined && !isNaN(max);

      if (!isNaN(numResult)) {
        if (hasMin && hasMax) {
          if (numResult > max) { result = `${numResult}(H)`; resultBold = true; }
          else if (numResult < min) { result = `${numResult}(L)`; resultBold = true; }
        } else if (hasMin && numResult < min) {
          result = `${numResult}(L)`; resultBold = true;
        } else if (hasMax && numResult > max) {
          result = `${numResult}(H)`; resultBold = true;
        }
      }

      const testObj = { testName, result, units, refRange };
      if (org?.orgCode === ORG_CODES.MANASWINI) testObj.comment = comment;
      if (bold) testObj.bold = true;
      if (boldname) testObj.boldname = true;
      testObj.resultBold = resultBold;

      testArray.push(testObj);
    }

    reportArray[templateId] = { [templateId]: testArray };
  }

  async function generateReport(index, rows) {
    rows[index]["value"][0] = rows[index]["value"][0].map((cell) => ({
      text: cell,
      fontSize: 11,
      bold: true,
    }));

    let getDocDefinition;
    let docDefinition;

    if (org?.orgCode === ORG_CODES.MANASWINI) {
      getDocDefinition = (await import("../../manaswini/docdeflabreportprint")).getDocDefinition;
      docDefinition = getDocDefinition({ patientInfo, doctorDetails, formattedDate, rows });
    } else if (org?.orgCode === ORG_CODES.MNH) {
      getDocDefinition = (await import("../../mnh/docdeflabreportprint")).getDocDefinition;
      docDefinition = getDocDefinition({ patientInfo, doctorDetails, rows, location, validators });
    } else {
      console.error("Invalid organization. Cannot load doc definition.");
      return;
    }

    console.log("All reports ready, initiating print...");

    if (printPreviewType === PRINT_FILE) {
      const htmlContent =
        org?.orgCode === ORG_CODES.MANASWINI
          ? convertDocDefinitionToHTMLManaswini(docDefinition)
          : convertDocDefinitionToHTML(docDefinition);

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      iframe.style.top = "0";
      iframe.style.left = "0";
      iframe.style.zIndex = "-1";
      iframe.id = `print-iframe-${Date.now()}`;

      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(htmlContent);
      iframeDoc.close();

      let hasPrinted = false;
      let loadTimeout = null;

      const triggerPrint = () => {
        if (hasPrinted) return;
        hasPrinted = true;
        if (loadTimeout) { clearTimeout(loadTimeout); loadTimeout = null; }
        const iframeWin = iframe.contentWindow;
        try { iframeWin.focus(); iframeWin.print(); } catch (e) { console.error(e); }
        const removeIframe = () => {
          try { if (document.body.contains(iframe)) document.body.removeChild(iframe); } catch (e) {}
        };
        if ("onafterprint" in iframeWin) iframeWin.onafterprint = removeIframe;
        setTimeout(removeIframe, 5000);
      };

      iframe.onload = () => setTimeout(() => { if (!hasPrinted) triggerPrint(); }, 800);
      loadTimeout = setTimeout(() => { if (!hasPrinted && document.body.contains(iframe)) triggerPrint(); }, 3000);

    } else if (printPreviewType === PRINT_PREVIEW) {
      PdfPrinter.createPdf(docDefinition).open();
    }
  }

  function convertUTCToISTDateTime(dispatchDate) {
    return new Date(dispatchDate).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  return <></>;
}

const parseRange = (refRange) => {
  if (!refRange) return { min: null, max: null };
  const clean = refRange.toLowerCase().trim();
  const upToMatch = clean.match(/up to\s*([\d,]+\.?\d*)/i);
  if (upToMatch) return { min: null, max: parseFloat(upToMatch[1].replace(/,/g, "")) };
  const rangeMatches = [...clean.matchAll(/(-?[\d,]+(?:\.\d+)?)\s*(?:-|–|to)\s*(-?[\d,]+(?:\.\d+)?)/gi)];
  if (rangeMatches.length > 0) {
    const first = rangeMatches[0];
    return { min: parseFloat(first[1].replace(/,/g, "")), max: parseFloat(first[2].replace(/,/g, "")) };
  }
  if (clean.startsWith("<")) return { min: null, max: parseFloat(clean.replace(/[^\d.]/g, "")) };
  if (clean.startsWith(">")) return { min: parseFloat(clean.replace(/[^\d.]/g, "")), max: null };
  const singleMatch = clean.match(/([\d,]+\.?\d*)/);
  if (singleMatch) { const num = parseFloat(singleMatch[1].replace(/,/g, "")); return { min: num, max: num }; }
  return { min: null, max: null };
};

export default LabReportPrint;